import { useEffect, useState, useCallback, useRef, Fragment } from 'react';
import { shallow } from 'zustand/shallow';
import { useFiltersStore, useMapViewStore, useTextSearchStore, useCityStore } from '../stores';
import Content from './../components/Content';
import HomeBanner from './HomeBanner';
import ShowrunnerMap from './../components/ShowRunnerMap';
import FeedFiltersPane from './FeedFiltersPane';
import FeedHeader from './FeedHeader';
import {
  MEDIUM_TAGS,
  TOP_TAGS,
  STYLE_TAGS,
  getLocationTagsForCity,
  ON_VIEW_TAGS,
  TYPE_OPTIONS,
  FUTURE_FAIR_SHOW_TYPE,
  trendingShowsLayer,
  regularShowsLayer,
  FUTURE_FAIR_FAVICON,
} from '../util/data-factory';
import { logShowImpression } from '../util/events';
import { getShows } from '../util/util';
import { getTypeTagsForCity, getHomeFilterTagsForCity } from '../util/util';
import { totalSelectedFilterCount as sumSelectedFilters } from '../util/filter-counts';

const Feed = ({ mode = 'list' }) => {
  const [featureData, setFeatureData] = useState();
  const [shows, setShows] = useState([]);
  const [recommendedShows, setRecommendedShows] = useState([]);
  const [error, setError] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  /** Bumps when a new fetch starts; stale responses must not overwrite state (overlapping requests). */
  const fetchGenerationRef = useRef(0);
  const [showFiltersPane, setShowFiltersPane] = useState(false);
  const [typeFilterOptions, setTypeFilterOptions] = useState(TYPE_OPTIONS);
  const [homeFilterOptions, setHomeFilterOptions] = useState(['Opening Soon', 'Opening Today']);
  const {
    open,
    showType,
    selectedMediums,
    selectedLocation,
    selectedSubject,
    selectedStyles,
    sortedByRelevance,
    resetFilters,
  } = useFiltersStore();

  const filterCountSnapshot = useFiltersStore(
    s => ({
      openCount: s.open.size,
      selectedMediumsCount: s.selectedMediums.size,
      selectedStylesCount: s.selectedStyles.size,
      selectedSubjectCount: s.selectedSubject.size,
      selectedLocationCount: s.selectedLocation.size,
      showTypeCount: s.showType.size,
    }),
    shallow
  );
  const { query, resetTextQuery } = useTextSearchStore();
  const { mainMapViewState, setMainMapViewState } = useMapViewStore();
  const { selectedCity } = useCityStore();

  // Get location tags based on selected city
  const LOCATION_TAGS = getLocationTagsForCity(selectedCity);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [types, home] = await Promise.all([
          getTypeTagsForCity(selectedCity),
          getHomeFilterTagsForCity(selectedCity),
        ]);
        if (cancelled) return;

        const baseTypes = types.length ? types : TYPE_OPTIONS;
        const nextTypes = baseTypes.includes(FUTURE_FAIR_SHOW_TYPE)
          ? baseTypes
          : [...baseTypes, FUTURE_FAIR_SHOW_TYPE];

        const baseHome = home.length ? home : ['Opening Soon', 'Opening Today'];
        // Keep Future Fair out of header pills (still available as a type filter if present).
        const nextHome = baseHome.filter(t => t !== FUTURE_FAIR_SHOW_TYPE);

        setTypeFilterOptions(nextTypes);
        setHomeFilterOptions(nextHome);
      } catch (e) {
        console.error('Failed to load filter tags for feed', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedCity]);

  // TODO: Use and object with after pasring the data
  const [mapPopupInfo, setMapPopupInfo] = useState();
  const [mapImageInfo, setMapImageInfo] = useState();
  const [mapTopTagsInfo, setMapTopTagsInfo] = useState();

  /**
   * Fetch shows from the database based on the selected filters.
   * Updates when filters change using the useFiltersStore.
   * so filters are persistent, across app. Uses getShows api.
   */
  const fetchShows = useCallback(async () => {
    const gen = ++fetchGenerationRef.current;
    setIsFetching(true);
    try {
      const data = await getShows({
        open,
        sortedByRelevance,
        selectedMediums,
        selectedLocation,
        selectedSubject,
        selectedStyles,
        textSearch: query,
        showType,
        city: selectedCity,
      });
      if (gen !== fetchGenerationRef.current) return;

      const list = Array.isArray(data) ? data : [];
      setShows(list);
      setRecommendedShows(list.filter(show => show.recommended === true));
      setError(null);
    } catch (err) {
      if (gen !== fetchGenerationRef.current) return;
      console.error(err);
      setError('Failed to fetch shows');
    } finally {
      if (gen === fetchGenerationRef.current) {
        setIsFetching(false);
      }
    }
  }, [
    open,
    sortedByRelevance,
    selectedLocation,
    selectedMediums,
    selectedSubject,
    selectedStyles,
    showType,
    query,
    selectedCity,
  ]);

  /**
   * Fetch shows when the component mounts.
   * If there is a query, search by query.
   * default is to fetch shows.
   */
  useEffect(() => {
    fetchShows();
  }, [fetchShows]);

  /**
   * Format filtered data as features to be
   * read as clusters by map-gl. Updates when filtered
   * data changes.
   */
  useEffect(() => {
    if (!Array.isArray(shows)) {
      setFeatureData({ type: 'FeatureCollection', features: [] });
      return;
    }
    const formatted_data = shows
      .filter(i => i?.venues != null)
      .map(i => {
        const longitude = parseFloat(i.venues.longitude);
        const latitude = parseFloat(i.venues.latitude);
        return {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [longitude, latitude],
          },
          properties: { ...i, lat: latitude, lng: longitude },
        };
      })
      .filter(f => Number.isFinite(f.geometry.coordinates[0]) && Number.isFinite(f.geometry.coordinates[1]));

    setFeatureData({ type: 'FeatureCollection', features: formatted_data });
  }, [shows]);

  /**
   * Handles logic when a filter is selected.
   * Adds or removes the selected filter from the store,
   * uses Map() object to store the selected filters.
   */
  const filterMap = {
    MEDIUM: {
      options: MEDIUM_TAGS,
      selected: selectedMediums,
    },
    'ON VIEW': {
      options: ON_VIEW_TAGS,
      selected: open,
    },
    NEIGHBORHOOD: {
      options: LOCATION_TAGS,
      selected: selectedLocation,
    },
    SUBJECT: {
      options: TOP_TAGS,
      selected: selectedSubject,
    },
    STYLE: {
      options: STYLE_TAGS,
      selected: selectedStyles,
    },
    TYPE: {
      options: typeFilterOptions,
      selected: showType,
    },
  };

  /** Sum of selected items across all filter sections (funnel badge). */
  const totalSelectedFilterCount = sumSelectedFilters(filterCountSnapshot);

  // TODO: Handle error
  //if (error) {
  //  return <div>Error: {error}</div>;
  //}

  /**
   * Handles logic when click event on cluster and markers
   */
  const onPinClick = useCallback((event, mapRef) => {
    const feature = event.features && event.features[0];
    if (!feature) return;
    const layer = feature.layer;

    if (layer.id === trendingShowsLayer.id || layer.id === regularShowsLayer.id) {
      //const mapboxSource = mapRef.current.getSource('my-data');
      //console.debug(mapboxSource);
      //console.log(feature.properties);
      event.originalEvent.stopPropagation();
      setMapPopupInfo({ ...feature.properties, venues: JSON.parse(feature.properties.venues) });
      setMapImageInfo(JSON.parse(feature.properties.image_urls));
      setMapTopTagsInfo(JSON.parse(feature.properties.top_tags ?? '[]'));
      void logShowImpression([feature.properties.show_id], 'map');
      if (mapRef.current) {
        mapRef.current.flyTo({
          center: [feature.properties.lng, feature.properties.lat],
          essential: true, // Instant
          curve: 1.5, // Smooth animation
          duration: 1000, // Transition duration
          padding: {
            top: 0,
            bottom: 300,
            left: 150,
            right: 0,
          },
        });
      }
    } else {
      event.originalEvent.stopPropagation();
    }
  }, []);

  /**
   * Handles logic when moving on the map, triggers on
   * zooming and panning.
   */
  const onMove = useCallback(
    event => {
      const { latitude, longitude, zoom } = event.viewState;
      setMainMapViewState({ latitude, longitude, zoom });
    },
    [setMainMapViewState]
  );

  const closeShowMapDialog = () => {
    setMapPopupInfo(null);
    setMapImageInfo(null);
    setMapTopTagsInfo(null);
  };

  const header = (
    <FeedHeader
      homeFilterOptions={homeFilterOptions}
      typeFilterOptions={typeFilterOptions}
      locationTags={LOCATION_TAGS}
      onToggleFilters={() => setShowFiltersPane(v => !v)}
      totalSelectedFilterCount={totalSelectedFilterCount}
    />
  );

  return (
    <Fragment>
      {mode === 'list' ? (
        <div className="relative min-h-screen bg-white">
          {header}

          <div className="-mt-2 md:-mt-1">
            <Content shows={shows} />
          </div>

          <div className="sticky bottom-[68px] z-40 md:bottom-0">
            <HomeBanner hidden={showFiltersPane} />
          </div>
        </div>
      ) : (
        <div className="w-full h-auto">
          {header}
          <div className="relative h-[calc(100vh-260px)] min-h-[420px] md:h-[80vh]">
            <ShowrunnerMap
              mainMapViewState={mainMapViewState}
              featureData={featureData}
              mapImageInfo={mapImageInfo}
              mapPopupInfo={mapPopupInfo}
              mapTopTagsInfo={mapTopTagsInfo}
              onPinClick={onPinClick}
              onMove={onMove}
              closeShowMapDialog={closeShowMapDialog}
            />
          </div>
        </div>
      )}

      <FeedFiltersPane
        isOpen={showFiltersPane}
        onClose={() => setShowFiltersPane(false)}
        isFetching={isFetching}
        filterMap={filterMap}
        shows={shows}
        recommendedShows={recommendedShows}
        onResetFilters={() => {
          resetFilters();
          resetTextQuery();
        }}
      />
    </Fragment>
  );
};

export default Feed;

// Other Map styles
// mapStyle="https://raw.githubusercontent.com/go2garret/maps/main/src/assets/json/openStreetMap.json" // https://basemaps.cartocdn.com/gl/positron-nolabels-gl-style/style.json
// mapStyle="https://external.xx.fbcdn.net/maps/vt/style/grayscale/"
